import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  ChevronRight, ChevronLeft, Check, Loader2, CheckCircle,
  AlertCircle, Shield,
} from 'lucide-react';

import { STEPS } from './constants';
import { emptyProperty, emptyAuto, emptyRecUnit, emptySpouse } from './helpers';

import Step1Declarations from './steps/Step1Declarations';
import Step2Personal from './steps/Step2Personal';
import Step3Properties from './steps/Step3Properties';
import Step4Vehicles from './steps/Step4Vehicles';
import Step5VehicleDetails from './steps/Step5VehicleDetails';
import Step6Liability from './steps/Step6Liability';

export default function IntakeForm() {
  const { token } = useParams();
  const [step, setStep] = useState(1);
  const [clientName, setClientName] = useState('');
  const [tokenValid, setTokenValid] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Step 1 — Declarations upload + AI analysis
  const [declFiles, setDeclFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [analyzed, setAnalyzed] = useState(false);
  const [aiFields, setAiFields] = useState([]);
  const fileInputRef = useRef();

  // Step 2 — Personal + spouse + drivers
  const [personal, setPersonal] = useState({
    prefix: '', firstName: '', middleInitial: '', lastName: '', suffix: '',
    dateOfBirth: '', gender: '',
    driversLicenseNumber: '', driversLicenseState: '',
    occupation: '', employer: '', maritalStatus: '', phone: '',
    spendMonthsOutOfState: false,
    takesAutosOutOfState: false,
    licensedAnotherState: false,
    priorLicenseState: '',
    priorLicenseNumber: '',
  });
  const [spouse, setSpouse] = useState(emptySpouse());
  const [drivers, setDrivers] = useState([]);

  // Step 3 — Properties (with AI-extracted markers)
  const [properties, setProperties] = useState([emptyProperty()]);
  const [aiExtractedProps, setAiExtractedProps] = useState([]);

  // Step 4 — Vehicles + recreational units
  const [autos, setAutos] = useState([emptyAuto()]);
  const [aiExtractedAutos, setAiExtractedAutos] = useState([]);
  const [recreationalUnits, setRecreationalUnits] = useState([]);

  // Step 6 — Liability
  const [liability, setLiability] = useState({
    hasUmbrella: false, hasRentalHomes: false, rentalHomesCount: '',
    jewelryOver10k: false, fineArtOver50k: false,
    // Conditional toggles drive whether the dollar fields are shown.
    // Not persisted directly — the agent infers these from non-zero amounts.
    ownsJewelry: false, ownsFineArt: false, ownsCoins: false,
    ownsWine: false, ownsMusical: false, ownsMisc: false,
    // Dollar amounts (DECIMAL on backend; empty string here = null on save)
    jewelryScheduledAmount: '', jewelryBlanketAmount: '', jewelryInBankVault: '',
    fineArtScheduledAmount: '', fineArtBlanketAmount: '',
    coinsScheduledAmount: '', coinsBlanketAmount: '',
    wineScheduledAmount: '', wineBlanketAmount: '',
    musicalScheduledAmount: '', musicalBlanketAmount: '',
    miscScheduledAmount: '',
    // Phase 2: per-category follow-up details
    // HVI = Highest Valued Item ($). Appraised = bool|null. Storage = 'home'|'home_safe'|'bank_vault'|'mixed'|null
    jewelryHVI: '',  jewelryAppraised: null,  jewelryStorage: null,
    fineArtHVI: '',  fineArtAppraised: null,  fineArtStorage: null,
    coinsHVI: '',    coinsAppraised:   null,  coinsStorage:   null,
    wineHVI: '',     wineAppraised:    null,  wineStorage:    null,
    musicalHVI: '',  musicalAppraised: null,  musicalStorage: null,
  });

  const hasSpouse = ['Married', 'Domestic Partner'].includes(personal.maritalStatus);

  // ─────────────────────────────────────────
  // TOKEN VERIFICATION ON LOAD
  // ─────────────────────────────────────────
  useEffect(() => {
    fetch(`/api/intake/${token}/verify`)
      .then(r => r.json())
      .then(data => {
        setTokenValid(data.valid);
        if (data.valid) setClientName(data.clientName || '');
      })
      .catch(() => setTokenValid(false));
  }, [token]);

  // ─────────────────────────────────────────
  // AI ANALYSIS (run when client uploads dec pages)
  // ─────────────────────────────────────────
  const runAnalysis = async (files) => {
    if (!files || files.length === 0) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    setAnalyzed(false);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await fetch(`/api/intake/${token}/analyze`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Analysis failed');
      const data = await res.json();

      if (data.personal) {
        const { phone: _phone, ...personalWithoutPhone } = data.personal;
        setPersonal(prev => ({
          ...prev,
          ...Object.fromEntries(Object.entries(personalWithoutPhone).filter(([_, v]) => v)),
        }));
      }
      if (data.properties && data.properties.length > 0) {
        const aiProps = data.properties.map(p => ({ ...emptyProperty(), ...p }));
        setProperties(aiProps);
        setAiExtractedProps(aiProps.map((_, i) => i));
      }
      if (data.autos && data.autos.length > 0) {
        const fullName = [personal.firstName, personal.lastName].filter(Boolean).join(' ');
        setAutos(data.autos.map(a => ({
          ...emptyAuto(), ...a,
          titleHolder: a.titleHolder || fullName,
          primaryDriver: a.primaryDriver || fullName,
        })));
        setAiExtractedAutos(data.autos.map((_, i) => i));
      }
      if (data.policies) setAiFields(data.policies);
      setAnalyzed(true);

      // Re-apply name to autos once personal is in state
      setPersonal(prev => {
        const fullName = [prev.firstName, prev.lastName].filter(Boolean).join(' ');
        if (fullName) {
          setAutos(prevAutos => prevAutos.map(a => ({
            ...a,
            titleHolder: a.titleHolder || fullName,
            primaryDriver: a.primaryDriver || fullName,
          })));
        }
        return prev;
      });
    } catch {
      setAnalyzeError('Could not analyze documents. You can continue and fill in the form manually.');
    } finally {
      setAnalyzing(false);
    }
  };

  // ─────────────────────────────────────────
  // STEP 1 HANDLERS
  // ─────────────────────────────────────────
  const handleDeclUpload = (e) => {
    const newFiles = Array.from(e.target.files);
    if (!newFiles.length) return;
    const updated = [...declFiles, ...newFiles];
    setDeclFiles(updated);
    setAnalyzed(false);
    runAnalysis(updated);
  };

  const removeDeclFile = (index) => {
    const updated = declFiles.filter((_, i) => i !== index);
    setDeclFiles(updated);
    setAnalyzed(false);
    if (updated.length > 0) runAnalysis(updated);
  };

  // ─────────────────────────────────────────
  // SUBMIT
  // ─────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (declFiles.length > 0) {
        const fd = new FormData();
        declFiles.forEach(f => fd.append('files', f));
        await fetch(`/api/intake/${token}/upload-declarations`, { method: 'POST', body: fd });
      }
      const res = await fetch(`/api/intake/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          personal,
          spouse: hasSpouse ? spouse : null,
          properties, autos, recreationalUnits, liability, drivers,
        }),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────
  // PROPERTY / AUTO / REC UNIT HANDLERS
  // ─────────────────────────────────────────
  const updateProperty = (index, updated) => setProperties(prev => prev.map((p, i) => i === index ? updated : p));
  const addProperty    = () => setProperties(prev => [...prev, emptyProperty()]);
  const removeProperty = (index) => setProperties(prev => prev.filter((_, i) => i !== index));

  const updateAuto = (index, key, val) => setAutos(prev => prev.map((a, i) => i === index ? { ...a, [key]: val } : a));
  const addAuto = () => {
    const fullName = [personal.firstName, personal.lastName].filter(Boolean).join(' ');
    setAutos(prev => [...prev, { ...emptyAuto(), titleHolder: fullName, primaryDriver: fullName }]);
  };
  const removeAuto = (index) => setAutos(prev => prev.filter((_, i) => i !== index));

  // Auto-fill title holder / primary driver as the client types their name
  useEffect(() => {
    const fullName = [personal.firstName, personal.lastName].filter(Boolean).join(' ');
    if (!fullName) return;
    setAutos(prev => prev.map(a => ({
      ...a,
      titleHolder: a.titleHolder || fullName,
      primaryDriver: a.primaryDriver || fullName,
    })));
  }, [personal.firstName, personal.lastName]);

  const updateRecUnit = (index, key, val) => setRecreationalUnits(prev => prev.map((u, i) => i === index ? { ...u, [key]: val } : u));
  const addRecUnit    = () => setRecreationalUnits(prev => [...prev, emptyRecUnit()]);
  const removeRecUnit = (index) => setRecreationalUnits(prev => prev.filter((_, i) => i !== index));

  // ─────────────────────────────────────────
  // EARLY RETURNS — loading / invalid token / submitted
  // ─────────────────────────────────────────
  if (tokenValid === null) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="animate-spin text-blue-600" size={32} />
    </div>
  );

  if (!tokenValid) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-10 max-w-md w-full text-center">
        <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <AlertCircle size={28} className="text-red-500" />
        </div>
        <h1 className="text-xl font-bold text-slate-800 mb-2">Link Expired or Invalid</h1>
        <p className="text-sm text-slate-500">This intake form link is no longer valid. Please contact your insurance agent for a new link.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-10 max-w-md w-full text-center">
        <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={28} className="text-green-500" />
        </div>
        <h1 className="text-xl font-bold text-slate-800 mb-2">All Done!</h1>
        <p className="text-sm text-slate-500">Your information has been submitted. Your agent will be in touch shortly.</p>
      </div>
    </div>
  );

  // ─────────────────────────────────────────
  // MAIN UI
  // ─────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <Shield size={16} className="text-white" />
          </div>
          <span className="font-semibold text-slate-800">ProRately</span>
        </div>
        {clientName && (
          <span className="text-sm text-slate-500">
            For: <span className="font-medium text-slate-700">{clientName}</span>
          </span>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-3 sm:px-4 py-6 sm:py-10">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Insurance Intake Form</h1>
          <p className="text-slate-500 text-sm">Please complete all sections so your agent can find the best coverage for you.</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-8 relative">
          <div className="absolute top-4 h-px bg-slate-200 z-0" style={{ left: '2rem', right: '2rem' }} />
          {STEPS.map((s) => {
            const Icon = s.icon;
            const done = step > s.id;
            const active = step === s.id;
            return (
              <div key={s.id} className="flex flex-col items-center z-10" style={{ flex: 1 }}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                  done ? 'bg-blue-600 border-blue-600 text-white' :
                  active ? 'bg-white border-blue-600 text-blue-600' :
                  'bg-white border-slate-300 text-slate-400'
                }`}>
                  {done ? <Check size={14} strokeWidth={3} /> : <Icon size={14} />}
                </div>
                <span className={`mt-1.5 text-xs font-medium hidden sm:block ${active ? 'text-blue-600' : done ? 'text-slate-600' : 'text-slate-400'}`}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>

        <div className="space-y-4">
          {step === 1 && (
            <Step1Declarations
              declFiles={declFiles}
              fileInputRef={fileInputRef}
              analyzing={analyzing}
              analyzed={analyzed}
              analyzeError={analyzeError}
              aiFields={aiFields}
              onUpload={handleDeclUpload}
              onRemoveFile={removeDeclFile}
              onSkip={() => setStep(2)}
            />
          )}

          {step === 2 && (
            <Step2Personal
              personal={personal}
              setPersonal={setPersonal}
              spouse={spouse}
              setSpouse={setSpouse}
              drivers={drivers}
              setDrivers={setDrivers}
            />
          )}

          {step === 3 && (
            <Step3Properties
              properties={properties}
              aiExtractedProps={aiExtractedProps}
              onUpdate={updateProperty}
              onAdd={addProperty}
              onRemove={removeProperty}
            />
          )}

          {step === 4 && (
            <Step4Vehicles
              autos={autos}
              aiExtractedAutos={aiExtractedAutos}
              personal={personal}
              onUpdateAuto={updateAuto}
              onAddAuto={addAuto}
              onRemoveAuto={removeAuto}
              recreationalUnits={recreationalUnits}
              onUpdateRecUnit={updateRecUnit}
              onAddRecUnit={addRecUnit}
              onRemoveRecUnit={removeRecUnit}
            />
          )}

          {step === 5 && (
            <Step5VehicleDetails autos={autos} updateAuto={updateAuto} />
          )}

          {step === 6 && (
            <Step6Liability liability={liability} setLiability={setLiability} error={error} />
          )}
        </div>

        {/* Nav */}
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-white border border-slate-200 transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={15} /> Back
          </button>
          <span className="text-xs text-slate-400">Step {step} of {STEPS.length}</span>
          {step < 6 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={step === 1 && analyzing}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50"
            >
              Next <ChevronRight size={15} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}